/**
 * "Export CSV" of the daily columns (MB, Q135, 18 Oct 2026; Teslyn Field's and, R246, Teslyn Cloud's): TJR's `_DailyCols.txt` is replaced by a CSV of the same
 * Daily Flight columns, written only when the crew presses the button, never automatically. Every entry of the
 * project in flying order, with the figures of the day view (`daySummary` in @teslyn/domain: landing, hours flown,
 * Prog TTIS, km flown and accepted) and the entry's own fields. Numbers keep the screens' decimals, without
 * thousands separators, so a spreadsheet reads them as numbers.
 */
import { daySummary, entryDates } from './day-summary.js';
import type { Aircraft, Block, DailyEntry, Flight, LineStatus, Person } from './model.js';
import { entryCrewName, shortName, type EntryCrewSlot } from './staff.js';

export interface DailyCsvData {
  readonly entries: readonly DailyEntry[];
  readonly aircraft: readonly Aircraft[];
  readonly people: readonly Pick<Person, 'id' | 'name'>[];
  readonly statuses: readonly LineStatus[];
  readonly flights: readonly Flight[];
  readonly blocks: readonly Block[];
}

export const DAILY_CSV_COLUMNS = [
  'Date',
  'Day',
  'Flt',
  'Aircraft',
  'Entry',
  'Standby type',
  'Days',
  'T/O',
  'Land',
  'Survey (h)',
  'Comp box (h)',
  'Cals (h)',
  'Ferry (h)',
  'External (h)',
  'Flown (h)',
  'Prog TTIS (h)',
  'Km flown',
  'Km accepted',
  'Lines',
  'Fuel used (L)',
  'Refuel source',
  'Refuel (L)',
  'Fuel location',
  'Pilots',
  'Operators',
  'Blocks',
  'Operational comment',
  'QC comment',
] as const;

const TYPE: Readonly<Record<DailyEntry['entry_type'], string>> = {
  flight: 'Flight',
  standby: 'Standby',
  maintenance: 'Maintenance',
  suspended: 'Suspended',
};
const STANDBY: Readonly<Record<string, string>> = {
  diurnal: 'Diurnal',
  weather: 'Weather',
  pilot_rdo: 'Pilot RDO',
  other: 'Other',
};
const SOURCE: Readonly<Record<string, string>> = { bowser: 'Bowser', drum: 'Drum', truck: 'Truck' };

/** One CSV field: quoted when it holds a comma, a quote or a line break (RFC 4180). */
export function csvField(v: string): string {
  return /[",\r\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
}

const fx = (v: number | null, d: number): string => (v === null || !Number.isFinite(v) ? '' : v.toFixed(d));

/** Rows of the daily columns, header first, every entry in flying order. */
export function dailyCsvRows(data: DailyCsvData): string[][] {
  const rego = (id: string | null): string => data.aircraft.find((a) => a.id === id)?.registration ?? '';
  // a person removed for good from the register keeps the name the entry kept (decision 602)
  const names = (e: DailyEntry, slots: readonly EntryCrewSlot[]): string =>
    slots
      .map((slot) => entryCrewName(data.people, e, slot))
      .filter((n): n is string => n !== null)
      .map(shortName)
      .join(', ');
  const blockNames = (ids: readonly string[]): string =>
    ids
      .map((id) => data.blocks.find((b) => b.id === id)?.name)
      .filter((n): n is string => n !== undefined)
      .join(', ');
  const rows: string[][] = [[...DAILY_CSV_COLUMNS]];
  for (const date of [...entryDates(data.entries)].reverse()) {
    const day = daySummary(date, data.entries, data.aircraft, data.statuses, data.flights);
    for (const r of day.rows) {
      const e = r.entry;
      const flight = e.entry_type === 'flight';
      rows.push([
        e.date,
        String(e.jday),
        e.flt_num === null ? '' : String(e.flt_num),
        rego(e.aircraft_id),
        TYPE[e.entry_type],
        e.standby_type ? (STANDBY[e.standby_type] ?? e.standby_type) : '',
        flight ? '' : fx(e.days_alloc, 2),
        e.takeoff_time ?? '',
        r.landing ?? '',
        fx(e.svy_hours, 1),
        fx(e.cbx_hours, 1),
        fx(e.cal_hours, 1),
        fx(e.fry_hours, 1),
        fx(e.ext_hours, 1),
        fx(r.hours_flown_h, 1),
        fx(r.prog_ttis_h, 1),
        fx(r.flown_km, 2),
        fx(r.accepted_km, 2),
        String(r.lines),
        fx(e.fuel_used_l, 0),
        e.fuel_source ? (SOURCE[e.fuel_source] ?? e.fuel_source) : '',
        fx(e.fuel_load_l, 0),
        e.fuel_location,
        names(e, ['pilot', 'pilot2']),
        names(e, ['operator1', 'operator2']),
        blockNames(e.blocks),
        e.op_comment,
        e.qc_comment,
      ]);
    }
  }
  return rows;
}

/** The CSV text: CRLF line ends and a UTF-8 byte order mark, so Excel opens it with the right characters. */
export function dailyCsv(data: DailyCsvData): string {
  return `\uFEFF${dailyCsvRows(data)
    .map((r) => r.map(csvField).join(','))
    .join('\r\n')}\r\n`;
}
